import { useCallback, useState } from 'react'
import { graphql, compose } from 'react-apollo'
import {
  Button,
  Form,
  FormGroup,
  Input,
  Label,
  Card,
  CardBody,
  CustomInput,
  Row,
  Col
} from 'reactstrap'
import Joi from '@hapi/joi'
import { validate } from '~/lib/helpers/validate'
import { TOS_MYSCP_EULA_ID } from '~/lib/constants/tos'
import { CreateOktaUserClient } from '~/queries/public.gql'
import useAlert from '~/hooks/useAlert'
import useLock from '~/hooks/useLock'
import BreadcrumbBar from '~/components/shared/BreadcrumbBar'
import InvalidFeedback from '~/components/shared/InvalidFeedback'

const createOktaUserClientValidator = Joi.object({
  terms: Joi.boolean().required().valid(true).messages({ 'any.only': 'Please read terms and check agree to continue' }),
  globalId: Joi.number().integer().min(1).required(),
  zip: Joi.string().trim().min(5).max(20).regex(/^(\d|[a-z]|[A-Z]| |-){5,20}$/).message('must be 5-20 characters in valid zip code format').required(),
  phone: Joi.string().min(10).max(13).regex(/^(\d{10}|\d{12})$/).message('must be 10 or 12 digits long (no dashes, spaces, plus signs etc.)').required(),
  password: Joi.string().regex(/^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9]).{12,72}$/).message('must be minimum of 12 characters, at least 1 uppercase letter, 1 lowercase letter, 1 numeric character, 1 special character, and cannot contain your name'),
  passwordConfirm: Joi.string().required().valid(Joi.ref('password')).messages({ 'any.only': 'must match password' })
})

function ClientAccess ({ create }) {
  const [alert, , setDanger] = useAlert()
  const [errors, setErrors] = useState(null)
  const [username, setUsername] = useState(null)
  const [loginState, setLoginState] = useState({
    terms: false,
    globalId: '',
    zip: '',
    phone: '',
    password: '',
    passwordConfirm: ''
  })

  const breadcrumbs = [
    { name: 'Login Help', href: '/login-help' },
    { name: 'Client Access', href: '/login-help/client-access' }
  ]
  let results = null

  // Handle form change
  const handleChange = useCallback(event => {
    setLoginState({
      ...loginState,
      [event.target.name]: event.target.type === 'checkbox' ? event.target.checked : event.target.value
    })
  }, [loginState])

  // Handle submission
  const [handleSubmit, locked] = useLock(useCallback(async () => {
    // Validate and transform
    const { value, errors: newErrors } = validate(createOktaUserClientValidator, loginState)
    setErrors(newErrors)
    if (newErrors) {
      setDanger('Please review errors and try again.')
      return
    }

    // Submit login
    try {
      value.terms = undefined
      const { data: { createOktaUserClient } } = await create(value)

      if (createOktaUserClient.valid) {
        setUsername(createOktaUserClient.username)
      } else {
        setDanger('The system was unable to validate your information. Confirm that the information you entered is correct and try again.')
      }
    } catch (error) {
      if (error.networkError && error.networkError.result && error.networkError.result.message) {
        setDanger(error.networkError.result.message)
      } else {
        setDanger(`Error submitting: ${error.message}`)
      }
    }
  }, [loginState]))

  if (username) {
    results = (
      <>
        <Row>
          <Col md='1' className='text-center m-auto'>
            <img className='mb-4 mb-md-0' src='/static/img/client-access.png' />
          </Col>
          <Col>
            <h2 className='pb-2'>
              You now have access to the SCP Health Client Portal!
            </h2>
            <p>
              Your username is <b>{username}</b>. You can use it to log in at <a href='https://client-portal.myscp.com/' target='_blank' rel='noopener noreferrer'>client-portal.myscp.com</a>.
            </p>
            <p>
              However, <b>please be aware that it may take up to 2 hours for our systems to synchronize and grant you access</b>. If you encounter any issues during your initial login attempt, we recommend trying again after a couple of hours.
            </p>
            <p>
              For help with any other issues, please email <a href='mailto:clientportalsupport@scphealth.com'>clientportalsupport@scphealth.com</a>.
            </p>
          </Col>
        </Row>
      </>
    )
  } else {
    results = (
      <>
        <Row>
          <Col md='1' className='text-center m-auto'>
            <img className='mb-4 mb-md-0' src='/static/img/client-access.png' />
          </Col>
          <Col>
            <p>
              Welcome to the Client Portal Registration Page.
            </p>
            <p>
              Please enter the necessary information below to verify your identity. All information must match what we have on file in order for access to be granted. <b>Please reference the registration email</b> for additional details.
            </p>
            <p>
              If you are <b>resetting your password</b>, please enter the required information and choose a new password. Note: you cannot reuse one of the previous ten passwords set.
            </p>
            <p>
              For help at any time, please contact IT support at <a href='tel:8883541411' target='_new'>888-354-1411</a>.
            </p>
          </Col>
        </Row>
        <hr />
        {alert}
        <Form noValidate autoComplete='off'>
          <FormGroup>
            <Label for='global-id'>Global ID</Label>
            <Input
              type='text'
              name='globalId'
              id='global-id'
              value={loginState.globalId}
              onChange={handleChange}
              invalid={errors && !!errors.globalId}
              maxLength='8'
              autoComplete='off'
            />
            <InvalidFeedback errors={errors} field='globalId' />
            <small className='text-muted'>
            Your Global ID serves as your unique identifier within SCP Health. This number is located on your registration email.
            </small>
          </FormGroup>

          <FormGroup>
            <Label for='zip'>ZIP Code</Label>
            <Input
              type='text'
              name='zip'
              id='zip'
              value={loginState.zip}
              onChange={handleChange}
              invalid={errors && !!errors.zip}
              maxLength='20'
              autoComplete='off'
            />
            <InvalidFeedback errors={errors} field='zip' />
            <small className='text-muted'>
              Please enter the ZIP Code of your mailing address.
            </small>
          </FormGroup>

          <FormGroup>
            <Label for='phone'>Phone Number</Label>
            <Input
              type='text'
              name='phone'
              id='phone'
              value={loginState.phone}
              onChange={handleChange}
              invalid={errors && !!errors.phone}
              maxLength='12'
              autoComplete='off'
            />
            <InvalidFeedback errors={errors} field='phone' />
            <small className='text-muted'>
              Please enter one of the phone numbers you provided to SCP. Exclude all formatting (ex. 3371234567)
            </small>
          </FormGroup>

          <FormGroup>
            <Label for='password'>Password</Label>
            <Input
              type='password'
              name='password'
              id='password'
              value={loginState.password}
              onChange={handleChange}
              invalid={errors && !!errors.password}
              maxLength='72'
              autoComplete='off'
            />
            <InvalidFeedback errors={errors} field='password' />
            <small className='text-muted'>
              Your password must include a minimum of 12 characters, featuring at least one uppercase letter, one lowercase letter, one number, and one special character. It should not include your name. Please note: you cannot reuse your last 10 passwords, and your password needs to be updated every 90 days.
            </small>
          </FormGroup>

          <FormGroup>
            <Label for='password-confirm'>Confirm Password</Label>
            <Input
              type='password'
              name='passwordConfirm'
              id='password-confirm'
              value={loginState.passwordConfirm}
              onChange={handleChange}
              invalid={errors && !!errors.passwordConfirm}
              maxLength='72'
              autoComplete='off'
            />
            <InvalidFeedback errors={errors} field='passwordConfirm' />
          </FormGroup>

          <FormGroup className='mb-0 mr-0' check inline>
            <CustomInput
              type='checkbox'
              name='terms'
              id='terms'
              value={loginState.terms}
              onChange={handleChange}
            />
            <Label for='terms' check>
              I agree to the <a href={`/tos/${TOS_MYSCP_EULA_ID}`} target='_blank' rel='noopener noreferrer'>Terms of Use</a>.
            </Label>
          </FormGroup>
          <div className='mt-n1 mb-4'>
            <InvalidFeedback errors={errors} field='terms' />
          </div>

          <FormGroup>
            <Button color='success' className='mr-3' onClick={handleSubmit} disabled={locked}>Submit</Button>
          </FormGroup>
          <small className='text-muted font-italic'>Except as described in the SCP Health Privacy Policy, we will not disclose your personal information outside of SCP and its controlled subsidiaries and affiliates without your consent.</small>
        </Form>
      </>
    )
  }

  return (
    <>
      <BreadcrumbBar breadcrumbs={breadcrumbs} />

      <h2 className='pb-2'>Client Access</h2>

      <Card>
        <CardBody>
          {results}
        </CardBody>
      </Card>
    </>
  )
}

ClientAccess.publicAccess = true

ClientAccess.getInitialProps = () => ({
  title: 'Client Access'
})

const withCreateOktaUserClient = graphql(CreateOktaUserClient, {
  name: 'create',
  props: ({ create }) => ({
    create: input => create({
      variables: { input }
    })
  })
})

export default compose(
  withCreateOktaUserClient
)(ClientAccess)
